export interface FileBlockItem {
  url: string
  name: string
  mimeType: string
  size?: number
}

type FileBlockInput = Partial<FileBlockItem>

export type FileBlockMarkdownSegment =
  | {
      type: 'markdown'
      markdown: string
    }
  | {
      type: 'fileBlock'
      file: FileBlockItem
    }

const FILE_BLOCK_PATTERN = /\[\[file\b([^\]]*)\]\]/g
const FILE_BLOCK_HTML_PATTERN = /<file-block\b([^>]*)>\s*<\/file-block>|<file-block\b([^>]*)\/>/g
const FILE_BLOCK_PLACEHOLDER = 'KARAKURI_FILE_BLOCK'

export const createDefaultFileBlockItem = (): FileBlockItem => ({
  url: '',
  name: '',
  mimeType: '',
})

export function normalizeFileBlockItem(item: FileBlockInput): FileBlockItem {
  return {
    url: item.url?.trim() ?? '',
    name: item.name?.trim() ?? '',
    mimeType: item.mimeType?.trim() ?? '',
    size: normalizeSize(item.size),
  }
}

export function encodeFileBlockItem(item: FileBlockInput): string {
  return encodeURIComponent(JSON.stringify(normalizeFileBlockItem(item)))
}

export function decodeFileBlockItem(encodedItem: string | undefined): FileBlockItem {
  if (!encodedItem) {
    return createDefaultFileBlockItem()
  }

  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(encodedItem))
    if (!isFileBlockItemLike(parsed)) {
      return createDefaultFileBlockItem()
    }

    return normalizeFileBlockItem(parsed)
  } catch {
    return createDefaultFileBlockItem()
  }
}

export function serializeFileBlockMarkdown(item: FileBlockInput): string {
  return `[[file data="${escapeShortcodeAttribute(encodeFileBlockItem(item))}"]]`
}

export function splitFileBlocksFromMarkdown(markdown: string): FileBlockMarkdownSegment[] {
  const segments: FileBlockMarkdownSegment[] = []
  let lastIndex = 0

  markdown.replace(
    FILE_BLOCK_PATTERN,
    (matchedText: string, attributes: string, offset: number) => {
      if (offset > lastIndex) {
        segments.push({ type: 'markdown', markdown: markdown.slice(lastIndex, offset) })
      }
      segments.push({ type: 'fileBlock', file: parseFileBlockAttributes(attributes) })
      lastIndex = offset + matchedText.length
      return matchedText
    }
  )

  if (lastIndex < markdown.length) {
    segments.push({ type: 'markdown', markdown: markdown.slice(lastIndex) })
  }

  return segments.length > 0 ? segments : [{ type: 'markdown', markdown }]
}

export function replaceFileBlockMarkdownWithPlaceholders(markdown: string): {
  markdown: string
  files: FileBlockItem[]
} {
  const files: FileBlockItem[] = []
  const replacedMarkdown = markdown.replace(
    FILE_BLOCK_PATTERN,
    (_matchedText, attributes: string) => {
      const index = files.length
      files.push(parseFileBlockAttributes(attributes))
      return `${FILE_BLOCK_PLACEHOLDER}_${index}`
    }
  )

  return { markdown: replacedMarkdown, files }
}

export function restoreFileBlockHtmlPlaceholders(html: string, files: FileBlockItem[]): string {
  return files.reduce((restoredHtml, file, index) => {
    const placeholder = `${FILE_BLOCK_PLACEHOLDER}_${index}`
    const fileBlockHtml = createFileBlockHtml(file)
    return restoredHtml
      .replace(new RegExp(`<p>\\s*${placeholder}\\s*</p>`, 'g'), fileBlockHtml)
      .replace(new RegExp(placeholder, 'g'), fileBlockHtml)
  }, html)
}

export function replaceFileBlockHtmlWithPlaceholders(html: string): {
  html: string
  files: FileBlockItem[]
} {
  const files: FileBlockItem[] = []
  const replacedHtml = html.replace(
    FILE_BLOCK_HTML_PATTERN,
    (_matchedText, attributesA: string | undefined, attributesB: string | undefined) => {
      const index = files.length
      files.push(decodeFileBlockItem(readHtmlAttribute(attributesA ?? attributesB ?? '')))
      return `<p>${FILE_BLOCK_PLACEHOLDER}_${index}</p>`
    }
  )

  return { html: replacedHtml, files }
}

export function restoreFileBlockMarkdownPlaceholders(
  markdown: string,
  files: FileBlockItem[]
): string {
  return files.reduce(
    (restoredMarkdown, file, index) =>
      restoredMarkdown.replace(
        createMarkdownPlaceholderRegExp(`${FILE_BLOCK_PLACEHOLDER}_${index}`),
        serializeFileBlockMarkdown(file)
      ),
    markdown
  )
}

function parseFileBlockAttributes(attributes: string): FileBlockItem {
  return decodeFileBlockItem(readShortcodeAttribute(attributes, 'data'))
}

function createFileBlockHtml(item: FileBlockItem): string {
  return `<file-block data-file="${escapeHtmlAttribute(encodeFileBlockItem(item))}"></file-block>`
}

function readShortcodeAttribute(attributes: string, name: string): string | undefined {
  const pattern = new RegExp(`${name}="((?:\\\\.|[^"\\\\])*)"`)
  const matched = pattern.exec(attributes)
  return matched ? unescapeShortcodeAttribute(matched[1]) : undefined
}

function readHtmlAttribute(attributes: string): string | undefined {
  const matched = /data-file=(?:"([^"]*)"|'([^']*)'|([^\s>]+))/.exec(attributes)
  return matched?.[1] ?? matched?.[2] ?? matched?.[3]
}

function escapeShortcodeAttribute(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

function unescapeShortcodeAttribute(value: string): string {
  return value.replace(/\\"/g, '"').replace(/\\\\/g, '\\')
}

function escapeHtmlAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;')
}

function createMarkdownPlaceholderRegExp(placeholder: string): RegExp {
  return new RegExp(escapeRegExp(placeholder).replace(/_/g, '\\\\?_'), 'g')
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function normalizeSize(size: unknown): number | undefined {
  if (size === undefined || size === null || size === '') {
    return undefined
  }
  const value = typeof size === 'number' ? size : Number(size)
  if (!Number.isFinite(value) || value < 0) {
    return undefined
  }
  return Math.round(value)
}

function isFileBlockItemLike(value: unknown): value is FileBlockInput {
  return typeof value === 'object' && value !== null
}
